"use client";

import React, { ErrorInfo, ReactNode } from "react";
import { flare } from "@flareapp/js";
import { shouldFilterFlareError } from "@/utils/monitoring/flareErrorFilter";
import { configureFlareClient } from "@/utils/monitoring/configureFlareClient";
import { safelyReportToFlare } from "@/utils/monitoring/safelyReportToFlare";
import {
  reloadForFreshDeployment,
  shouldRecoverFromStaleAssets,
  STALE_ASSET_RELOAD_MARKER,
} from "@/utils/monitoring/staleDeploymentRecovery";

// This must run on the @flareapp/js singleton at module load; constructor/useEffect
// timing is too late for hydration errors emitted while React builds the initial tree.
configureFlareClient(flare);

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

export class FlareErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (
      typeof window !== "undefined" &&
      shouldRecoverFromStaleAssets(error.message, error.stack || "")
    ) {
      reloadForFreshDeployment(
        window.location,
        window.sessionStorage,
        STALE_ASSET_RELOAD_MARKER,
      );
      return;
    }

    if (
      process.env.NEXT_PUBLIC_FLARE_API_KEY &&
      !shouldFilterFlareError(error)
    ) {
      safelyReportToFlare(flare, error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2">
                Something went wrong
              </h2>
              <p className="text-gray-600 mb-4">
                An unexpected error occurred.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Reload page
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
